import { fabric } from 'fabric';
import ControlBase from '../fabric/ControlBase';
import { renderOrderControl } from './controlRendering';
import { type RecordId, type DraggableItemContainer } from '../types';

const WIDTH = 23;
const HEIGHT = 16;
const MIN_DIMENSION = 20;

class DragReorderControl extends ControlBase {
  // Name of the action the control is used for.
  actionName = 'dragItem';
  // Whether this control lies on a row or column
  draggingVertical: boolean;
  // Index of item in the object
  id: RecordId;
  // fill color of dots in control object
  dotColor: string;
  // fill color of control
  controlColor: string;
  grabbing: boolean;
  // Whether the control is displayed to the left/top or right/bottom of the object
  leftTop: boolean;

  constructor({
    id,
    draggingVertical = true,
    leftTop = true,
  }: {
    id: RecordId;
    draggingVertical: boolean;
    leftTop?: boolean;
  }) {
    const offsetX = (draggingVertical ? -20 : 0) * (leftTop ? 1 : -1);
    const offsetY = (draggingVertical ? 0 : -20) * (leftTop ? 1 : -1);

    super({ offsetX, offsetY });
    this.id = id;
    this.draggingVertical = draggingVertical;
    this.cursorStyle = 'grab';
    this.leftTop = leftTop;

    if (this.draggingVertical) {
      this.sizeX = this.touchSizeX = HEIGHT;
      this.sizeY = this.touchSizeY = WIDTH;
    } else {
      this.sizeX = this.touchSizeX = WIDTH;
      this.sizeY = this.touchSizeY = HEIGHT;
    }

    this.setVisibility(false);
    this.cursorStyle = 'grab';
    this.actionHandler = this.actionHandler.bind(this);
    this.mouseUpHandler = this.mouseUpHandler.bind(this);
  }

  mouseUpHandler(eventData: MouseEvent, transform: fabric.Transform): boolean {
    this.cursorStyle = 'grab';
    const object = transform.target as DraggableItemContainer;
    if (object.updateItemOrder({ id: this.id })) {
      object.canvas.controller.undoManager.completeUndoGroup();
    }

    object.dragState = {
      left: undefined,
      top: undefined,
      placement: undefined,
    };
    this.dotColor = '#666';
    this.controlColor = 'white';

    return false;
  }

  private get defaultPosition() {
    return this.leftTop ? -0.5 : 0.5;
  }

  positionHandler(
    dim: { x: number; y: number },
    finalMatrix: unknown,
    fabricObject: DraggableItemContainer,
    currentControl: fabric.Control
  ): fabric.Point {
    const zoom = fabricObject.canvas?.getZoom() || 1;
    if (this.visible) {
      this.setVisibility(
        Math.min(
          fabricObject.width * fabricObject.scaleX,
          fabricObject.height * fabricObject.scaleY
        ) >
          MIN_DIMENSION / zoom
      );
    }

    const rect = fabricObject.draggableItemForId({
      id: this.id,
      draggingVertical: this.draggingVertical,
      scaled: true,
    });
    if (rect) {
      const { left, top, width, height } = rect;

      let controlLeft = left;
      let controlTop = top;

      // Adjust control position by object's border width
      const strokeWidth = fabricObject.strokeWidth ?? 0;
      if (this.draggingVertical) {
        controlTop += strokeWidth / 2;
      } else {
        controlLeft += strokeWidth / 2;
      }

      // If there is a drag happening, adjust item left or top appropriately
      if (this.draggingVertical && fabricObject.dragState.top) {
        controlTop = fabricObject.dragState.top * fabricObject.scaleY;
      } else if (!this.draggingVertical && fabricObject.dragState.left) {
        controlLeft = fabricObject.dragState.left * fabricObject.scaleX;
      }

      const adjustedPointer = fabric.util.transformPoint(
        {
          x: controlLeft + width / 2,
          y: controlTop + height / 2,
        } as fabric.Point,
        fabric.util.invertTransform(fabricObject.calcOwnMatrix())
      );

      const objectDimensions = fabricObject._getNonTransformedDimensions();

      let newX, newY;

      if (this.draggingVertical) {
        newX = this.defaultPosition;
        newY =
          fabricObject.dragState.top && this.isActive(fabricObject)
            ? (fabricObject.dragState.top + height / fabricObject.scaleY / 2) /
              objectDimensions.y
            : adjustedPointer.y / objectDimensions.y;
      } else {
        newX =
          fabricObject.dragState.left && this.isActive(fabricObject)
            ? (fabricObject.dragState.left + width / fabricObject.scaleX / 2) /
              objectDimensions.x
            : adjustedPointer.x / objectDimensions.x;
        newY = this.defaultPosition;
      }

      this.x = newX;
      this.y = newY;
    }

    return super.positionHandler(
      dim,
      finalMatrix,
      fabricObject,
      currentControl
    );
  }

  actionHandler(
    eventData: MouseEvent,
    transform: fabric.Transform,
    x: number,
    y: number
  ): boolean {
    this.dotColor = 'white';
    this.controlColor = '#0073CF';

    // Get this control's dimensions
    const object = transform.target as DraggableItemContainer;
    const draggedItemKey = this.draggingVertical ? 'top' : 'left';

    const rect = object.draggableItemForId({
      id: this.id,
      draggingVertical: this.draggingVertical,
      scaled: true,
    });

    if (rect) {
      const itemRect = rect;
      const offset = object.componentOffset();
      const dim = object._getTransformedDimensions();
      const localPoint = fabric.controlsUtils.getLocalPoint(
        transform,
        transform.originX,
        transform.originY,
        x,
        y
      );

      // Get point relative to object to determine where item ends up
      const adjustedPointer = fabric.util.transformPoint(
        { x, y } as fabric.Point,
        fabric.util.invertTransform(object.calcOwnMatrix())
      );

      // Use local 0 -> object dim point to calculate bounds
      const lowerLimit = this.draggingVertical
        ? localPoint.y >= itemRect.height / 2 + offset.y
        : localPoint.x >= itemRect.width / 2 + offset.x;
      const upperLimit = this.draggingVertical
        ? localPoint.y + itemRect.height / 2 <= dim.y
        : localPoint.x + itemRect.width / 2 <= dim.x;
      // Use object relative dimensions to actually set the item
      const draggedItemEndPoint = this.draggingVertical
        ? adjustedPointer.y - itemRect.height / object.scaleY / 2
        : adjustedPointer.x - itemRect.width / object.scaleX / 2;

      // Item was dragged passed ends of object, so place item right at end
      if (!lowerLimit) {
        const dim = object._getNonTransformedDimensions();
        const endPoint = this.draggingVertical
          ? -dim.y / 2 + offset.y
          : -dim.x / 2 + offset.x;
        object.dragState[draggedItemKey] = endPoint;
      } else if (!upperLimit) {
        const dim = object._getNonTransformedDimensions();
        const endPoint = this.draggingVertical
          ? dim.y / 2 - itemRect.height / object.scaleY
          : dim.x / 2 - itemRect.width / object.scaleX;
        object.dragState[draggedItemKey] = endPoint;
      } else {
        // Otherwise, just place item relative to cursor
        object.dragState[draggedItemKey] = draggedItemEndPoint;
      }

      // Set object end item id to indicate where dragged item will land
      object.dragState.placement = object.calculatePlacement({
        midpoint: { x, y },
        id: this.id,
        draggingVertical: this.draggingVertical,
      });

      return true;
    }

    return false;
  }

  render(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    styleOverride: unknown,
    fabricObject: fabric.Object
  ) {
    renderOrderControl(
      this,
      ctx,
      left,
      top,
      fabricObject,
      this.draggingVertical ? HEIGHT : WIDTH,
      this.draggingVertical ? WIDTH : HEIGHT,
      this.draggingVertical
    );
  }
}
export default DragReorderControl;
